import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Typography as MuiTypography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment";

/**
 * SubConversations Component
 * Handles display and interaction with subconversations in chat
 *
 * @param {Object} props
 * @param {Array} props.subConversations - Array of subconversation objects
 * @param {Function} props.onSubConversationSelect - Callback when subconversation is selected
 * @param {Function} props.onSubConversationMenuOpen - Callback for menu options
 * @param {string} props.selectedSubConversationId - Currently selected subconversation ID
 * @param {Function} props.onSelectionChange - Callback when selection changes
 */
const SubConversations = ({
  subConversations = [],
  onSubConversationSelect,
  onSubConversationMenuOpen,
  selectedSubConversationId,
  onSelectionChange,
}) => {
  // State for expand/collapse
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle subconversation selection
  const handleSubConversationSelect = (subConvId) => {
    if (selectedSubConversationId === subConvId) {
      // If already selected, deselect it
      onSelectionChange(null);
    } else {
      // Select the new subconversation
      onSelectionChange(subConvId);
    }
  };

  // Handle expand/collapse toggle
  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Sort sub-conversations: selected first, then by last message updatedAt or sub-conversation updatedAt
  const getSortedSubConversations = () => {
    if (!subConversations || subConversations.length === 0) {
      return [];
    }

    const sorted = [...subConversations].sort((a, b) => {
      // Selected sub-conversation always comes first
      if (a._id === selectedSubConversationId) return -1;
      if (b._id === selectedSubConversationId) return 1;

      // Get the most recent time for sorting (last message or sub-conversation update)
      const getLastUpdateTime = (subConv) => {
        // Priority 1: lastMessage updatedAt
        if (subConv?.lastMessage?.updatedAt) {
          return new Date(subConv.lastMessage.updatedAt).getTime();
        }
        // Priority 2: sub-conversation updatedAt
        if (subConv?.updatedAt) {
          return new Date(subConv.updatedAt).getTime();
        }
        // Priority 3: sub-conversation createdAt
        if (subConv?.createdAt) {
          return new Date(subConv.createdAt).getTime();
        }
        // Fallback: current time
        return new Date().getTime();
      };

      const aTime = getLastUpdateTime(a);
      const bTime = getLastUpdateTime(b);

      // Most recent first (higher timestamp = more recent)
      return bTime - aTime;
    });

    return sorted;
  };

  // Clear selection when subconversations change
  useEffect(() => {
    if (
      selectedSubConversationId &&
      !subConversations.find((sc) => sc._id === selectedSubConversationId)
    ) {
      onSelectionChange(null);
    }
  }, [subConversations, selectedSubConversationId, onSelectionChange]);

  // Don't render if no subconversations
  if (!subConversations || subConversations.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "#f8fafc",
        padding: { xs: 1.5, sm: 4, md: 4 },
        marginBottom: 1,
      }}
    >
      {/* Subconversations Cards Container */}
      <Box sx={{ position: "relative" }}>
        {/* Scroll Indicators - Show when content is scrollable */}
        {isExpanded && subConversations.length > 5 && (
          <>
            {/* Top indicator - shows when there's content above */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "20px",
                background: "linear-gradient(#f8fafc, transparent)",
                pointerEvents: "none",
                zIndex: 1,
                opacity: 0.8,
                transition: "opacity 0.3s ease",
                "&:hover": {
                  opacity: 1,
                },
              }}
            />
            {/* Bottom indicator - shows when there's content below */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "20px",
                background: "linear-gradient(transparent, #f8fafc)",
                pointerEvents: "none",
                zIndex: 1,
                opacity: 0.8,
                transition: "opacity 0.3s ease",
                "&:hover": {
                  opacity: 1,
                },
              }}
            />
          </>
        )}

        {/* Expand/Collapse Button - Positioned at bottom left like download arrow */}
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: "-30px", sm: "-55px" },
            left: { xs: "10px", sm: "15px" },
            zIndex: 2,
          }}
        >
          <IconButton
            size="small"
            onClick={handleExpandToggle}
            sx={{
              backgroundColor: "#6b7280",
              color: "#ffffff",
              padding: 1,
              borderRadius: 1,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#7C3AED",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
              },
            }}
          >
            {isExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>

        {/* Closed View - Overlapping Cards (First 3) */}
        {!isExpanded && (
          <Box sx={{ position: "relative", height: 120 }}>
            {getSortedSubConversations()
              .slice(0, 3)
              .map((subConv, index) => (
                <Box
                  key={subConv._id}
                  onClick={() => handleSubConversationSelect(subConv._id)}
                  sx={{
                    position: "absolute",
                    top: index * 15,
                    left: index * 8,
                    right: index * 8,
                    zIndex:
                      selectedSubConversationId === subConv._id
                        ? 10
                        : 3 - index,
                    backgroundColor: "#ffffff",
                    border:
                      selectedSubConversationId === subConv._id
                        ? "2px solid #7C3AED"
                        : "1px solid #e2e8f0",
                    borderRadius: 2,
                    padding: { xs: 1, sm: 1.5 },
                    boxShadow:
                      selectedSubConversationId === subConv._id
                        ? "0 4px 16px rgba(124, 58, 237, 0.25), 0 0 20px rgba(124, 58, 237, 0.15)"
                        : "0 2px 8px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    "&:hover": {
                      transform:
                        selectedSubConversationId === subConv._id
                          ? "translateY(-2px) scale(1.02)"
                          : "translateY(-2px)",
                      boxShadow:
                        selectedSubConversationId === subConv._id
                          ? "0 6px 20px rgba(124, 58, 237, 0.3)"
                          : "0 4px 12px rgba(0, 0, 0, 0.15)",
                      zIndex: 10,
                    },
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontSize: "0.7rem",
                          fontWeight: 500,
                        }}
                      >
                        {subConv?.lastMessage?.updatedAt
                          ? moment(subConv.lastMessage.updatedAt).fromNow()
                          : moment(subConv?.updatedAt).fromNow()}
                      </Typography>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation(); // Prevent card selection
                        onSubConversationMenuOpen?.(event, subConv);
                      }}
                      sx={{
                        padding: 0.5,
                        color: "#6b7280",
                        "&:hover": {
                          color: "#7C3AED",
                          backgroundColor: "rgba(124, 58, 237, 0.1)",
                        },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>

                  {/* Main Title */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#000000",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      fontWeight: 700,
                      mb: 0.5,
                      textTransform: "lowercase",
                      lineHeight: 1.2,
                    }}
                  >
                    {subConv.categoryId?.name}
                  </Typography>

                  {/* Last Message */}
                  {subConv?.lastMessage?.message && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b7280",
                        fontSize: "0.75rem",
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      {subConv.lastMessage.message}
                    </Typography>
                  )}

                  {/* Ticket Number and Time */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#6b7280",
                        fontSize: "0.7rem",
                      }}
                    >
                      ticket no. {subConv.ticketNo || subConv._id}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "#6b7280",
                        fontSize: "0.7rem",
                      }}
                    >
                      {moment(subConv?.lastMessage?.updatedAt).format(
                        "DD/MM/YYYY HH:mm"
                      )}
                    </Typography>
                  </Box>

                  {/* Status Message */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: subConv.status === "open" ? "#7C3AED" : "#7C3AED",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      textTransform: "lowercase",
                    }}
                  >
                    {subConv.status || "pending"}
                  </Typography>
                </Box>
              ))}
          </Box>
        )}

        {/* Expanded View - All Cards */}
        {isExpanded && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              maxHeight: { xs: "300px", sm: "350px", md: "400px" }, // Responsive max height
              overflowY: "auto", // Enable vertical scrolling
              overflowX: "hidden", // Hide horizontal scrollbar
              paddingRight: "8px", // Add padding for scrollbar
              scrollBehavior: "smooth", // Smooth scrolling
              // Always show scrollbar when content overflows
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#a8a8a8",
                },
              },
              // Firefox scrollbar styling
              scrollbarWidth: "thin",
              scrollbarColor: "#c1c1c1 #f1f1f1",
            }}
          >
            {getSortedSubConversations().map((subConv) => (
              <Box
                key={subConv._id}
                onClick={() => handleSubConversationSelect(subConv._id)}
                sx={{
                  backgroundColor: "#ffffff",
                  border:
                    selectedSubConversationId === subConv._id
                      ? "2px solid #7C3AED"
                      : "1px solid #e2e8f0",
                  borderRadius: 2,
                  padding: { xs: 1.5, sm: 2 },
                  boxShadow:
                    selectedSubConversationId === subConv._id
                      ? "0 4px 16px rgba(124, 58, 237, 0.25)"
                      : "0 2px 8px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  position: "relative",
                  "&:hover": {
                    borderColor:
                      selectedSubConversationId === subConv._id
                        ? "#7C3AED"
                        : "#7C3AED",
                    boxShadow:
                      selectedSubConversationId === subConv._id
                        ? "0 6px 20px rgba(124, 58, 237, 0.3)"
                        : "0 4px 12px rgba(124, 58, 237, 0.15)",
                  },
                }}
              >
                {/* Card Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 18, color: "#6b7280" }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b7280",
                        fontSize: "0.8rem",
                        fontWeight: 500,
                      }}
                    >
                      {subConv.time || "5 sec"}
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation(); // Prevent card selection
                      onSubConversationMenuOpen?.(event, subConv);
                    }}
                    sx={{
                      padding: 0.5,
                      color: "#6b7280",
                      "&:hover": {
                        color: "#7C3AED",
                        backgroundColor: "rgba(124, 58, 237, 0.1)",
                      },
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>

                {/* Main Title */}
                <Typography
                  variant="body1"
                  sx={{
                    color: "#000000",
                    fontSize: { xs: "1rem", sm: "1.125rem" },
                    fontWeight: 700,
                    mb: 0.5,
                    textTransform: "lowercase",
                    lineHeight: 1.3,
                  }}
                >
                  {subConv.title || subConv.categoryId?.name || "no title"}
                </Typography>

                {/* Last Message */}
                {subConv?.lastMessage?.message && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      fontSize: "0.875rem",
                      mb: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                  >
                    {subConv.lastMessage.message}
                  </Typography>
                )}

                {/* Ticket Number and Time */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      fontSize: "0.8rem",
                    }}
                  >
                    ticket no. {subConv.ticketNo || subConv._id}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      fontSize: "0.8rem",
                    }}
                  >
                    {subConv?.lastMessage?.updatedAt
                      ? moment(subConv.lastMessage.updatedAt).format(
                          "DD/MM/YYYY HH:mm"
                        )
                      : moment(subConv?.updatedAt).format("DD/MM/YYYY HH:mm")}
                  </Typography>
                </Box>

                {/* Status Message */}
                <Typography
                  variant="body2"
                  sx={{
                    color: subConv.status === "open" ? "#7C3AED" : "#7C3AED",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    textTransform: "lowercase",
                  }}
                >
                  {subConv.status || "pending"}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SubConversations;
